const parseStoredValue = (key) => {
    const rawValue = localStorage.getItem(key)
    if (!rawValue) return null

    try {
        return JSON.parse(rawValue)
    } catch (error) {
        console.error(`Error parsing ${key} from localStorage:`, error)
        return null
    }
}

const persistedUser = parseStoredValue('user')
const persistedAccessToken = parseStoredValue('access_token')
const persistedRefreshToken = parseStoredValue('refresh_token')

const isAnonymousUser = (user) =>
    user?.roles?.some((r) => r?.toLowerCase() === 'anonymous')

// Anonymous sessions are tab-scoped: sessionStorage is cleared on tab close.
// If we find an anonymous user in localStorage but no sessionStorage marker,
// the user closed the tab — treat that as a logged-out state.
const anonymousSessionActive = Boolean(sessionStorage.getItem('anonymous_session_active'))

// Detect when an anonymous user navigated away from the app (pagehide fired)
// and is now returning via a fresh page load (not a simple refresh).
const anonymousLeftApp = Boolean(sessionStorage.getItem('anonymous_left_app'))
const navType = performance?.getEntriesByType?.('navigation')?.[0]?.type
const anonymousReturnedFromOutside = anonymousLeftApp && navType !== 'reload'

// Tab closed — clean up stale anonymous tokens
if (persistedUser && isAnonymousUser(persistedUser) && !anonymousSessionActive) {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('user')
}

// Navigated away to another site and came back — force login
if (persistedUser && isAnonymousUser(persistedUser) && anonymousReturnedFromOutside) {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('user')
    sessionStorage.removeItem('anonymous_session_active')
    sessionStorage.removeItem('anonymous_left_app')
}

const hasValidPersistedSession = Boolean(
    persistedUser && persistedAccessToken && persistedRefreshToken
) && (!isAnonymousUser(persistedUser) || (anonymousSessionActive && !anonymousReturnedFromOutside))

const initialState = {
    user: hasValidPersistedSession ? persistedUser : null,
    accessToken: hasValidPersistedSession ? persistedAccessToken : null,
    refreshToken: hasValidPersistedSession ? persistedRefreshToken : null,
    isAuthenticated: hasValidPersistedSession,
    error: null,
}

export default function authReducer(state = initialState, action) {
    switch (action.type) {
        case 'LOGIN_SUCCESS':
            return {
                ...state,
                user: action.payload.user,
                accessToken: action.payload.accessToken,
                refreshToken: action.payload.refreshToken,
                isAuthenticated: true,
                error: null,
            }
        case 'LOGIN_FAILURE':
            return {
                ...state,
                user: null,
                accessToken: null,
                refreshToken: null,
                isAuthenticated: false,
                error: action.payload,
            }
        case 'LOGOUT':
            return {
                ...state,
                user: null,
                accessToken: null,
                refreshToken: null,
                isAuthenticated: false,
                error: null,
            }
        case 'UPDATE_TOKENS':
            return {
                ...state,
                accessToken: action.payload.accessToken,
                refreshToken: action.payload.refreshToken,
            }
        case 'UPDATE_USER':
            return {
                ...state,
                user: { ...state.user, ...action.payload },
            }
        default:
            return state
    }
} 